import compact from 'lodash/compact'
import {
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoCone,
  GeckoHorizontalTankShell,
  GeckoLegacyBoilerWalls,
  GeckoLegacyCone,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  GeckoPipeEccentricReducer,
  GeckoPipeElbow,
  GeckoPipeSection,
  GeckoPipeTJoint,
  GeckoTankFlatRoof,
  GeckoTankFloor,
  GeckoVerticalTankShell,
} from 'types'

export function getNominalRangeFromTankFlatRoofOrFloor(
  componentModel: GeckoTankFloor | GeckoTankFlatRoof
): GeckoMeasurementRange | undefined {
  if (!componentModel.baseNominalThickness) {
    return undefined
  }

  return GeckoMeasurementRange.CreateFromStartEnd(
    componentModel.baseNominalThickness,
    componentModel.baseNominalThickness
  )
}

export function getNominalRangeFromTankShell(
  componentModel: GeckoVerticalTankShell
): GeckoMeasurementRange | undefined {
  return getNominalRangeFromCourses(componentModel.coursesArray)
}

export function getNominalRangeFromLegacyCone(
  componentModel: GeckoLegacyCone | GeckoCone
): GeckoMeasurementRange | undefined {
  return getNominalRangeFromCourses(componentModel.coursesArray)
}

function getNominalRangeFromWallThickness(
  componentModel:
    | GeckoHorizontalTankShell
    | GeckoPipeSection
    | GeckoPipeElbow
    | GeckoPipeTJoint
    | GeckoPipeEccentricReducer
): GeckoMeasurementRange | undefined {
  if (!componentModel.wallNominalThickness) {
    return undefined
  }

  return GeckoMeasurementRange.CreateFromStartEnd(
    componentModel.wallNominalThickness,
    componentModel.wallNominalThickness
  )
}

export function getNominalRangeFromLegacyBoilerWalls(
  componentModel: GeckoLegacyBoilerWalls
): GeckoMeasurementRange | undefined {
  const nominals = compact(
    componentModel.wallsArray.map((wall) => wall.wallNominalThickness)
  )

  if (nominals.length === 0) {
    return undefined
  }

  const min = GeckoMeasurement.Min(...nominals)
  const max = GeckoMeasurement.Max(...nominals)

  return GeckoMeasurementRange.CreateFromStartEnd(min, max)
}

function getNominalRangeFromCourses(
  courses: { wallNominalThickness?: GeckoMeasurement }[]
): GeckoMeasurementRange | undefined {
  if (!courses || courses.length === 0) {
    return GeckoMeasurementRange.CreateFromStartEnd(
      GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Inches),
      GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Inches)
    )
  }

  const nominals = compact(courses.map((course) => course.wallNominalThickness))

  if (nominals.length === 0) {
    return undefined
  }

  const min = GeckoMeasurement.Min(...nominals)
  const max = GeckoMeasurement.Max(...nominals)

  return GeckoMeasurementRange.CreateFromStartEnd(min, max)
}

export const getComponentNominalRange = (
  componentModel: GeckoComponentModel
): GeckoMeasurementRange | undefined => {
  switch (componentModel.type) {
    case GeckoComponentModelType.VerticalTankShell:
      return getNominalRangeFromTankShell(componentModel)
    case GeckoComponentModelType.LegacyCone:
    case GeckoComponentModelType.Cone:
      return getNominalRangeFromLegacyCone(componentModel)
    case GeckoComponentModelType.HorizontalTankShell:
    case GeckoComponentModelType.PipeSection:
    case GeckoComponentModelType.PipeElbow:
    case GeckoComponentModelType.PipeTJoint:
    case GeckoComponentModelType.PipeEccentricReducer:
      return getNominalRangeFromWallThickness(componentModel)
    case GeckoComponentModelType.LegacyBoilerWalls:
      return getNominalRangeFromLegacyBoilerWalls(componentModel)
    case GeckoComponentModelType.TankFloor:
    case GeckoComponentModelType.TankFlatRoof:
      return getNominalRangeFromTankFlatRoofOrFloor(componentModel)
    default:
      return undefined
  }
}
